import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { VERSION_TERMINOS } from '../../estaticas/legal.js';
import { useSesion } from '../../../common/hooks/useSesion.js';
import { cerrarSesion } from '../../../common/services/auth.js';
import { guardarConsentimiento, obtenerPerfil, type PerfilDeCuenta } from '../../../common/services/cuentas.js';
import { CONSENTIMIENTOS_CAMBIADOS, descartarConsentimientoPendiente } from '../consentimientosPendientes.js';
import { ConectorAsistentes } from '../components/ConectorAsistentes.js';

const TEXTO_NEWSLETTER: Readonly<Record<Idioma, string>> = {
  es: 'Quiero recibir la newsletter de nuevos estudios y novedades de la industria (puede incluir información de productos de Sinapser).',
  en: "I want to receive the newsletter about new studies and industry news (may include information about Sinapser's products).",
};

/** El nivel, el correo, la newsletter (editable) y cerrar sesión. CUENTAS-01. */
export function MiCuentaPage({ idioma }: { readonly idioma: Idioma }) {
  const t = diccionarioDe(idioma).cuentas.miCuenta;
  const sesion = useSesion();
  const usuarioActual = useRef(sesion?.user.id);
  usuarioActual.current = sesion?.user.id;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [perfilUsuarioId, setPerfilUsuarioId] = useState<string | undefined>();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(false);
  const [perfil, setPerfil] = useState<PerfilDeCuenta | null | undefined>(undefined);

  useEffect(() => {
    setPerfil(undefined);
    setGuardando(false);
    setError(false);
    if (sesion === null || sesion === undefined) return;
    let vigente = true;
    let peticion = 0;
    const refrescar = () => {
      const actual = ++peticion;
      void obtenerPerfil(sesion.access_token).then(p => {
        if (vigente && actual === peticion) {
          setPerfil(p);
          setPerfilUsuarioId(sesion.user.id);
        }
      });
    };
    refrescar();
    window.addEventListener(CONSENTIMIENTOS_CAMBIADOS, refrescar);
    return () => {
      vigente = false;
      window.removeEventListener(CONSENTIMIENTOS_CAMBIADOS, refrescar);
    };
  }, [sesion]);

  if (sesion === null) return <Navigate to='/iniciar-sesion' replace />;
  if (sesion === undefined || perfil === undefined || perfilUsuarioId !== sesion.user.id) {
    return (
      <main className='mx-auto flex w-full max-w-[480px] grow items-center justify-center px-6 py-16'>
        <p className='m-0 font-(family-name:--text) text-[17px] text-(--ink-soft)'>{t.cargando}</p>
      </main>
    );
  }

  const cambiarNewsletter = (marcado: boolean) => {
    setGuardando(true);
    setError(false);
    void guardarConsentimiento(
      { tipo: 'newsletter', aceptado: marcado, textoVersion: VERSION_TERMINOS, texto: TEXTO_NEWSLETTER[idioma] },
      sesion.access_token,
    ).then(guardado => {
      if (usuarioActual.current !== sesion.user.id) return;
      setGuardando(false);
      if (guardado) {
        if (sesion.user.email) descartarConsentimientoPendiente(sesion.user.email, 'newsletter');
        setPerfil(p => (p === null ? p : p && { ...p, consentimientos: { ...p.consentimientos, newsletter: marcado } }));
      } else setError(true);
    });
  };

  return (
    // Ancha en escritorio (Iván, 2026-10-02): a 480 px la dirección del conector y los pasos quedaban apretados.
    <main className='mx-auto flex w-full max-w-[980px] grow flex-col gap-6 px-6 py-16'>
      <h1 className='m-0 font-(family-name:--serif) text-[30px] font-bold text-(--ink)'>{t.titulo}</h1>

      <div className='flex flex-col gap-4 min-[768px]:flex-row min-[768px]:items-start min-[768px]:justify-between min-[768px]:gap-10'>
        <dl className='m-0 grid grid-cols-[120px_1fr] gap-y-3'>
          <dt className='font-(family-name:--text) text-[16px] text-(--ink-faint)'>{t.correo}</dt>
          <dd className='m-0 font-(family-name:--text) text-[17px] break-all text-(--ink)'>{sesion.user.email}</dd>
          <dt className='font-(family-name:--text) text-[16px] text-(--ink-faint)'>{t.nivel}</dt>
          <dd className='m-0 font-(family-name:--text) text-[17px] text-(--ink)'>
            {perfil === null ? '' : perfil.nivel === 'pro' ? t.pro : t.normal}
          </dd>
        </dl>

        {perfil === null ? null : (
          <label className='flex max-w-[420px] items-start gap-2.5 font-(family-name:--text) text-[16px] text-(--ink-soft)'>
            <input
              type='checkbox'
              disabled={guardando}
              checked={perfil.consentimientos.newsletter}
              onChange={e => {
                cambiarNewsletter(e.target.checked);
              }}
              className='mt-0.5'
            />
            <span>{t.newsletter}</span>
          </label>
        )}
      </div>

      {error ? <p role='alert'>{t.errorNewsletter}</p> : null}

      {perfil?.puedeEnviarEstudios || perfil?.tieneHistorialEstudios ? (
        <Link to='/mis-estudios' className='w-fit rounded-full border border-(--line-strong) px-5 py-2 text-(--ink) hover:bg-(--chat-bg)'>
          {idioma === 'es' ? 'Mis estudios' : 'My studies'}
        </Link>
      ) : null}

      <ConectorAsistentes idioma={idioma} asistenteInicial={params.get('asistente')} />

      <button
        type='button'
        onClick={() => {
          void cerrarSesion().then(() => {
            void navigate('/');
          });
        }}
        className='w-fit cursor-pointer rounded-full border border-(--line-strong) px-5 py-2 font-(family-name:--text) text-[16px] text-(--ink) hover:bg-(--chat-bg)'
      >
        {t.cerrarSesion}
      </button>
    </main>
  );
}
